/**
 * Contract Overview — Frontend ローカル Contract
 *
 * BE 側 `dresscode-backend/v2/src/li-force/api/contract-overview/usecase/contract-overview.api-contract.ts`
 * と同じ Zod 構造を再現。BE が `@standandforce/api-contract` に publish された後、
 * client.ts でローカル import を置き換える想定。
 *
 * path は Controller プレフィックス `/v2/contract-ledger/contracts-overview` を含む絶対パス。
 * （BE 側は Controller プレフィックスからの相対パス `/count` 等として宣言しているが、
 *   FE の OpenAPILink は最終 URL を必要とするためフルパスで宣言する）
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';

/** ContractStatus enum — BE Prisma と同期 */
const ContractStatusEnum = z.enum(['DRAFT', 'PENDING_SIGNATURE', 'REJECTED_SIGNATURE', 'EXECUTED', 'WAITING', 'TERMINATED', 'CANCELLED']);

/** ContractType enum — BE Prisma と同期 */
const ContractTypeEnum = z.enum(['EMPLOYMENT', 'BOARD_MEMBER', 'SERVICE', 'LEASE', 'PURCHASE', 'SAAS_SUBSCRIPTION', 'OFFICE_RENTAL', 'OTHER']);

/** ContractCategory enum — BE Prisma と同期 */
const ContractCategoryEnum = z.enum(['HUMAN_RESOURCE', 'DEVICE', 'SERVICE', 'FACILITY', 'OTHER']);
const RecentContractSchema = z.object({
  id: z.string(),
  contractTitle: z.string(),
  contractStartDate: z.string()
});
const ContractTypeBreakdownSchema = z.object({
  contractType: ContractTypeEnum,
  count: z.number()
});
const ContractStatusBreakdownSchema = z.object({
  contractStatus: ContractStatusEnum,
  count: z.number()
});
const ContractTypeCountMapSchema = z.object({
  EMPLOYMENT: z.number(),
  BOARD_MEMBER: z.number(),
  SERVICE: z.number(),
  LEASE: z.number(),
  PURCHASE: z.number(),
  SAAS_SUBSCRIPTION: z.number(),
  OFFICE_RENTAL: z.number(),
  OTHER: z.number()
});
const ContractCategoryCountMapSchema = z.object({
  HUMAN_RESOURCE: z.number(),
  DEVICE: z.number(),
  SERVICE: z.number(),
  FACILITY: z.number(),
  OTHER: z.number()
});
const ContractOverviewCountResponseSchema = z.object({
  totalCount: z.number(),
  recentContracts: z.array(RecentContractSchema),
  executedCount: z.number(),
  executedTopContractTypes: z.array(ContractTypeBreakdownSchema),
  inProgressCount: z.number(),
  inProgressStatusBreakdown: z.array(ContractStatusBreakdownSchema),
  endedOrCancelledCount: z.number(),
  endedOrCancelledStatusBreakdown: z.array(ContractStatusBreakdownSchema),
  contractTypeCounts: ContractTypeCountMapSchema,
  contractCategoryCounts: ContractCategoryCountMapSchema
});
const ScheduledContractSchema = z.object({
  id: z.string(),
  contractTitle: z.string(),
  contractType: ContractTypeEnum,
  contractEndDate: z.string()
});
const ContractOverviewScheduleResponseSchema = z.object({
  items: z.array(ScheduledContractSchema)
});
const ContractActivitySchema = z.object({
  id: z.string(),
  eventType: z.string(),
  occurredAt: z.string(),
  aggregateId: z.string(),
  contractTitle: z.string().nullable()
});
const ContractOverviewActivityResponseSchema = z.object({
  items: z.array(ContractActivitySchema)
});
export const contractOverviewContract = oc.router({
  count: oc.route({
    method: 'GET',
    path: '/v2/contract-ledger/contracts-overview/count'
  }).output(ContractOverviewCountResponseSchema),
  schedule: oc.route({
    method: 'GET',
    path: '/v2/contract-ledger/contracts-overview/schedule'
  }).output(ContractOverviewScheduleResponseSchema),
  activity: oc.route({
    method: 'GET',
    path: '/v2/contract-ledger/contracts-overview/activity'
  }).output(ContractOverviewActivityResponseSchema)
});
export type ContractOverviewCountResponse = z.infer<typeof ContractOverviewCountResponseSchema>;
export type ContractOverviewScheduleResponse = z.infer<typeof ContractOverviewScheduleResponseSchema>;
export type ContractOverviewActivityResponse = z.infer<typeof ContractOverviewActivityResponseSchema>;
export type RecentContract = z.infer<typeof RecentContractSchema>;
export type ScheduledContract = z.infer<typeof ScheduledContractSchema>;
export type ContractActivity = z.infer<typeof ContractActivitySchema>;
export type ContractTypeValue = z.infer<typeof ContractTypeEnum>;
export type ContractCategoryValue = z.infer<typeof ContractCategoryEnum>;